import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box, Typography, Alert } from '@mui/material';
import { authService } from './services/auth';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { ThemeProvider as CustomThemeProvider, useTheme } from './contexts/ThemeContext';
import { NavigationProvider } from './contexts/NavigationContext';

// Eager: shell + auth
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import Unauthorized from './components/Unauthorized';

// Lazy: heavy admin pages
const Parts = React.lazy(() => import('./components/Parts'));
const Categories = React.lazy(() => import('./components/Categories'));
const Vehicles = React.lazy(() => import('./components/Vehicles'));
const Orders = React.lazy(() => import('./components/Orders'));
const Users = React.lazy(() => import('./components/Users'));
const Advertisements = React.lazy(() => import('./components/Advertisements'));
const Shipping = React.lazy(() => import('./components/Shipping'));
const Payments = React.lazy(() => import('./components/Payments'));
const Profile = React.lazy(() => import('./components/Profile'));
const ContactInfo = React.lazy(() => import('./components/ContactInfo'));
const SystemSettings = React.lazy(() => import('./components/SystemSettings'));
const CountryMades = React.lazy(() => import('./components/CountryMades'));
const Brands = React.lazy(() => import('./components/Brands'));
const AuthTest = React.lazy(() => import('./components/AuthTest'));
const MultilingualTest = React.lazy(() => import('./components/MultilingualTest'));

const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
    <CircularProgress />
  </Box>
);

// Create theme with dynamic direction and mode support
const getTheme = (isRTL: boolean, isDark: boolean) => createTheme({
  direction: isRTL ? 'rtl' : 'ltr',
  palette: {
    mode: isDark ? 'dark' : 'light',
    primary: {
      main: '#FF6500',
      light: '#FF8533',
      dark: '#E55A00',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: isDark ? '#B0B0B0' : '#424242',
      light: isDark ? '#E0E0E0' : '#6D6D6D',
      dark: isDark ? '#808080' : '#21201F',
      contrastText: '#FFFFFF',
    },
    background: {
      default: isDark ? '#121212' : '#FFFFFF',
      paper: isDark ? '#1E1E1E' : '#F5F5F5',
    },
    text: {
      primary: isDark ? '#FFFFFF' : '#21201F',
      secondary: isDark ? '#B0B0B0' : '#424242',
    },
    grey: {
      50: isDark ? '#2A2A2A' : '#FAFAFA',
      100: isDark ? '#3A3A3A' : '#F5F5F5',
      200: isDark ? '#4A4A4A' : '#EEEEEE',
      300: isDark ? '#5A5A5A' : '#E0E0E0',
      400: isDark ? '#6A6A6A' : '#BDBDBD',
      500: isDark ? '#7A7A7A' : '#9E9E9E',
      600: isDark ? '#8A8A8A' : '#757575',
      700: isDark ? '#9A9A9A' : '#616161',
      800: isDark ? '#AAAAAA' : '#424242',
      900: isDark ? '#BABABA' : '#21201F',
    },
    divider: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
  },
  typography: {
    fontFamily: isRTL 
      ? '"CustomArabic", "Noto Sans Arabic", "Almarai", "Cairo", "Roboto", sans-serif'
      : '"CustomLatin", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      color: isDark ? '#FFFFFF' : '#21201F',
    },
    h2: {
      color: isDark ? '#FFFFFF' : '#21201F',
    },
    h3: {
      color: isDark ? '#FFFFFF' : '#21201F',
    },
    h4: {
      color: isDark ? '#FFFFFF' : '#21201F',
    },
    h5: {
      color: isDark ? '#FFFFFF' : '#21201F',
    },
    h6: {
      color: isDark ? '#FFFFFF' : '#21201F',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
        contained: {
          boxShadow: isDark 
            ? '0 2px 8px rgba(255, 101, 0, 0.4)' 
            : '0 2px 8px rgba(255, 101, 0, 0.3)',
          '&:hover': {
            boxShadow: isDark 
              ? '0 4px 12px rgba(255, 101, 0, 0.5)' 
              : '0 4px 12px rgba(255, 101, 0, 0.4)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: isDark 
            ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
            : '0 2px 8px rgba(33, 32, 31, 0.1)',
          border: isDark 
            ? '1px solid rgba(255, 255, 255, 0.1)' 
            : '1px solid rgba(66, 66, 66, 0.1)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove default gradient in dark mode
        },
      },
    },
  },
});

// Theme-Aware Component (must be inside LanguageProvider and CustomThemeProvider)
const ThemeWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isRTL } = useLanguage();
  const { isDark } = useTheme();
  return (
    <ThemeProvider theme={getTheme(isRTL, isDark)}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

function App() {
  const [isDevelopmentMode, setIsDevelopmentMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    const isDev = process.env.NODE_ENV === 'development' && window.location.search.includes('dev=true');
    if (isDev) {
      localStorage.setItem('authToken', 'mock-token-for-development');
      localStorage.setItem('user', JSON.stringify({
        id: 1,
        email: 'admin@example.com',
        firstName: 'Admin',
        lastName: 'User',
        roles: ['Admin', 'Manager'],
        phoneNumber: '+1234567890',
        isActive: true
      }));
    }
    return isDev;
  });

  useEffect(() => {
    // Initialize authentication state
    authService.initializeAuth();
    
    // Check if we're in development mode (for testing without authentication)
    const isDev = process.env.NODE_ENV === 'development' && window.location.search.includes('dev=true');
    setIsDevelopmentMode(isDev);
    
    // If in development mode, set a mock user
    if (isDev) {
      const mockUser = {
        id: 1,
        email: 'admin@example.com',
        firstName: 'Admin',
        lastName: 'User',
        roles: ['Admin', 'Manager'],
        phoneNumber: '+1234567890',
        isActive: true
      };
      localStorage.setItem('authToken', 'mock-token-for-development');
      localStorage.setItem('user', JSON.stringify(mockUser));
    }
  }, []);

  // Development mode component that bypasses authentication
  const DevelopmentMode = () => (
    <CustomThemeProvider>
      <LanguageProvider>
        <ThemeWrapper>
          <NavigationProvider>
            <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Development routes - no authentication required */}
              <Route path="/dashboard" element={
                <Layout>
                  <Dashboard />
                </Layout>
              } />
              
              <Route path="/parts" element={
                <Layout>
                  <Parts />
                </Layout>
              } />
              
              <Route path="/categories" element={
                <Layout>
                  <Categories />
                </Layout>
              } />
              
              <Route path="/brands" element={
                <Layout>
                  <Brands />
                </Layout>
              } />
              
              <Route path="/countries-made" element={
                <Layout>
                  <CountryMades />
                </Layout>
              } />
              
              <Route path="/vehicles" element={
                <Layout>
                  <Vehicles />
                </Layout>
              } />
              
              <Route path="/orders" element={
                <Layout>
                  <Orders />
                </Layout>
              } />
              
              <Route path="/users" element={
                <Layout>
                  <Users />
                </Layout>
              } />
              
              <Route path="/advertisements" element={
                <Layout>
                  <Advertisements />
                </Layout>
              } />
              
              <Route path="/contact-info" element={
                <Layout>
                  <ContactInfo />
                </Layout>
              } />
              
              <Route path="/shipping" element={
                <Layout>
                  <Shipping />
                </Layout>
              } />
              
              <Route path="/payments" element={
                <Layout>
                  <Payments />
                </Layout>
              } />
              
              
              <Route path="/settings" element={
                <Layout>
                  <SystemSettings />
                </Layout>
              } />
              
              <Route path="/profile" element={
                <Layout>
                  <Profile />
                </Layout>
              } />
              
              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
          </NavigationProvider>
        </ThemeWrapper>
      </LanguageProvider>
    </CustomThemeProvider>
  );

  // If in development mode, show the development version
  if (isDevelopmentMode) {
    return <DevelopmentMode />;
  }

  return (
    <CustomThemeProvider>
      <LanguageProvider>
        <ThemeWrapper>
          <NavigationProvider>
            <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            {process.env.NODE_ENV === 'development' && (
              <>
                <Route path="/auth-test" element={<AuthTest />} />
                <Route path="/multilingual-test" element={<MultilingualTest />} />
              </>
            )}
            {/* Protected routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/parts" element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager']}>
                <Layout>
                  <Parts />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/categories" element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager']}>
                <Layout>
                  <Categories />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/brands" element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager']}>
                <Layout>
                  <Brands />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/countries-made" element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager']}>
                <Layout>
                  <CountryMades />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/vehicles" element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager']}>
                <Layout>
                  <Vehicles />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/orders" element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager']}>
                <Layout>
                  <Orders />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/users" element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <Layout>
                  <Users />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/advertisements" element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager']}>
                <Layout>
                  <Advertisements />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/contact-info" element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <Layout>
                  <ContactInfo />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/shipping" element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager']}>
                <Layout>
                  <Shipping />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/payments" element={
              <ProtectedRoute requiredRoles={['Admin', 'Manager']}>
                <Layout>
                  <Payments />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <Layout>
                  <SystemSettings />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/profile" element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Suspense>
          </BrowserRouter>
          </NavigationProvider>
        </ThemeWrapper>
      </LanguageProvider>
    </CustomThemeProvider>
  );
}

export default App;
